import { faFacebook } from '@fortawesome/free-brands-svg-icons/faFacebook'
import { faGoogle } from '@fortawesome/free-brands-svg-icons/faGoogle'
import { faEllipsisVertical } from '@fortawesome/pro-regular-svg-icons/faEllipsisVertical'
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope'
import {
  ActionIcon,
  Menu,
  Text,
  Flex,
  LoadingOverlay,
  Box,
  type BoxProps,
  Anchor,
} from '@mantine/core'
import { useMemo } from 'react'

import { useUserLogout } from '@flecto/auth'
import {
  useMutationDeleteUserIdentity,
  useQueryGetAllIdentities,
  useMutationResendEmailVerification,
  useMutationChangeMainIdentity,
  useMutationForgotIdentityPassword,
} from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreUserIdentity } from '@flecto/types'
import {
  useMediaQuery,
  useNotifications,
  useModal,
  Badge,
  Icon,
} from '@flecto/ui'

import { ModalChangeEmail } from '../ModalChangeEmail/ModalChangeEmail'
import { ModalConfirmationMainIdentity } from '../ModalConfirmationMainIdentity/ModalConfirmationMainIdentity'
import { ModalLinkGoogleCalendar } from '../ModalLinkGoogleCalendar/ModalLinkGoogleCalendar'
import { useGoogleCalendarActions } from '../ModalLinkGoogleCalendar/useGoogleCalendarActions'
import { ModalUnlinkIdentity } from '../ModalUnlinkIdentity/ModalUnlinkIdentity'
import styles from './styles.module.css'

export type RowIdentityProps = BoxProps & {
  identity: CoreUserIdentity
  is_main: boolean
  loading?: boolean
}

export function RowIdentity({
  identity,
  is_main,
  loading = false,
  ...props
}: RowIdentityProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'profile_account' })
  const { isMobile } = useMediaQuery()
  const { showNotification } = useNotifications()
  const logout = useUserLogout()

  const { mutateAsync: resendEmailVerificationMutation } =
    useMutationResendEmailVerification()

  const { mutate: onUnlinkIdentity, isLoading: isDeleteLoading } =
    useMutationDeleteUserIdentity()
  const { mutateAsync: changeMainIdentity, isLoading: isChanging } =
    useMutationChangeMainIdentity()

  const { mutateAsync: forgotIdentityPassword } =
    useMutationForgotIdentityPassword()

  const { data } = useQueryGetAllIdentities()

  const { unlinkGoogleCalendar, isLoading: isTogglingGoogleCalendar } =
    useGoogleCalendarActions({ identity })

  const handleForgotPassword = async () => {
    try {
      await forgotIdentityPassword({ auth0_id: identity.auth0_id })
      showNotification({
        type: 'success',
        message: t('notifications.forgot_password_success'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notifications.alert_error_general'),
      })
    }
  }

  const confirmChangeMain = async () => {
    try {
      await changeMainIdentity({ auth0_id: identity.auth0_id })

      logout({
        logoutParams: {
          returnTo: window.location.origin,
        },
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notifications.alert_error_general'),
      })
    }
  }

  const onResendEmail = async () => {
    try {
      await resendEmailVerificationMutation({ auth0_id: identity.auth0_id })

      showNotification({
        type: 'success',
        message: t('notifications.alert_success_link_will_be_sent'),
      })
    } catch {
      showNotification({
        type: 'error',
        message: t('notifications.alert_error_general'),
      })
    }
  }

  const { openModal } = useModal(
    <ModalConfirmationMainIdentity
      onConfirm={confirmChangeMain}
      email={identity.email}
    />
  )

  const { openModal: openUnlinkModal } = useModal(
    <ModalUnlinkIdentity
      onConfirm={() => onUnlinkIdentity({ auth0_id: identity.auth0_id })}
    />
  )

  const { openModal: openModalChangeEmail } = useModal(
    <ModalChangeEmail identity={identity} />
  )

  const { openModal: openModalLinkGoogleCalendar } = useModal(
    <ModalLinkGoogleCalendar identity={identity} />
  )

  const handleGoogleCalendarLink = async () => {
    openModalLinkGoogleCalendar()
    return
  }

  const handleGoogleCalendarUnlink = async () => {
    await unlinkGoogleCalendar()
  }

  const isLocalLoading =
    isDeleteLoading || isChanging || isTogglingGoogleCalendar
  const isUsernamePassword = identity.provider === 'auth0'
  const isGoogleCalendarLinked = identity?.google_calendar_enabled

  const hasGoogleCalendarActive = useMemo(
    () =>
      data?.user_identity?.find(
        (identity) => identity?.google_calendar_enabled
      ),
    [data]
  )

  const providers = new Map([
    ['auth0', t('card_linked_accounts.providers.email')],
    ['google-oauth2', t('card_linked_accounts.providers.google')],
    ['facebook', t('card_linked_accounts.providers.facebook')],
  ])

  const icons = new Map([
    ['auth0', faEnvelope],
    ['google-oauth2', faGoogle],
    ['facebook', faFacebook],
  ])

  const icon = icons.get(identity.provider) ?? faEnvelope

  const provider = providers.get(identity.provider) ?? ''

  const verifyBadge = (
    <Badge color={identity.email_verified ? 'teal' : 'red'}>
      {t(
        identity.email_verified
          ? 'card_linked_accounts.verified'
          : 'card_linked_accounts.not_verified'
      )}
    </Badge>
  )

  const providerBadge = provider ? (
    <Badge color='gray' leftSection={icon && <Icon icon={icon} />}>
      {provider}
    </Badge>
  ) : (
    <></>
  )

  return (
    <Box {...props} className={styles.rowIdentity} data-cy='identity-card'>
      <LoadingOverlay visible={loading || isLocalLoading} />
      <Flex justify='space-between' align='center'>
        <Flex gap='md' align='center'>
          <Flex direction='column'>
            <Flex gap='sm' align='center'>
              <Text
                maw={isMobile ? 190 : 'auto'}
                aria-label='email'
                style={{
                  overflowWrap: 'break-word',
                }}
                fw={500}
              >
                {identity.email}
              </Text>

              {!isMobile && verifyBadge}
            </Flex>
            <Flex align='center' gap='sm' wrap='wrap'>
              <Text
                truncate
                c='gray'
                style={{
                  overflowWrap: 'break-word',
                }}
                maw={isMobile ? 190 : 'auto'}
              >
                {t('card_linked_accounts.authenticated_with', {
                  replace: {
                    provider: providers.get(identity.provider),
                  },
                })}
              </Text>
              {identity.provider === 'google-oauth2' &&
                isGoogleCalendarLinked && (
                  <>
                    <Text c='dimmed'>&bull;</Text>
                    <Text c='gray'>
                      {t('card_linked_accounts.google_calendar_linked')}
                    </Text>
                    <Anchor onClick={openModalLinkGoogleCalendar}>
                      {t('card_linked_accounts.view_settings')}
                    </Anchor>
                  </>
                )}
            </Flex>
            {isMobile && (
              <Flex gap='sm' mt={'sm'} direction={'column'}>
                <Box>{verifyBadge}</Box>
                <Box>
                  <Badge color='blue'>
                    {t('card_linked_accounts.main_identity')}
                  </Badge>
                </Box>
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex align='center' gap='md'>
          {is_main && !isMobile && (
            <Badge color='blue'>
              {t('card_linked_accounts.main_identity')}
            </Badge>
          )}
          {providerBadge}
          <Menu position='bottom-end' withinPortal>
            <Menu.Target>
              <ActionIcon variant='default' loading={false} size='sm'>
                <Icon icon={faEllipsisVertical} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={openModalChangeEmail}
                disabled={!isUsernamePassword}
              >
                {t('card_linked_accounts.update_identity_email')}
              </Menu.Item>
              <Menu.Item disabled={is_main} onClick={openModal}>
                {t('card_linked_accounts.set_as_main')}
              </Menu.Item>
              <Menu.Item
                disabled={!isUsernamePassword}
                onClick={() => handleForgotPassword()}
              >
                {t('card_linked_accounts.forgot_password')}
              </Menu.Item>
              <Menu.Item
                disabled={identity.email_verified}
                onClick={() => onResendEmail()}
              >
                {t('card_linked_accounts.resend_email_verification')}
              </Menu.Item>
              {identity.provider === 'google-oauth2' && (
                <>
                  {!isGoogleCalendarLinked && (
                    <Menu.Item onClick={handleGoogleCalendarLink}>
                      {t('card_linked_accounts.link_google_calendar')}
                    </Menu.Item>
                  )}
                  {isGoogleCalendarLinked && (
                    <Menu.Item onClick={handleGoogleCalendarUnlink}>
                      {t('card_linked_accounts.unlink_google_calendar')}
                    </Menu.Item>
                  )}
                  <Menu.Item
                    onClick={openModalLinkGoogleCalendar}
                    disabled={!hasGoogleCalendarActive}
                  >
                    {t('card_linked_accounts.change_google_calendar_settings')}
                  </Menu.Item>
                </>
              )}
              <Menu.Item disabled={is_main} onClick={() => openUnlinkModal()}>
                {t('card_linked_accounts.unlink_accout')}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Flex>
      </Flex>
    </Box>
  )
}
